import { useCallback, useEffect, useRef, useState } from "react";
import { getCsrfHeader } from "../lib/csrf";
import {
  INTEGRATION_SLOT_ITEM_CODE,
  type MutationResult,
  type SlotEntitlement,
  type Subscription,
} from "../types/billing";

// Billing for a single subscription, keyed directly by its Stripe id (the fleet
// Billing tab's detail modal). Unlike useTenantBilling it doesn't go via the
// tenant lookup, so it also drives subscriptions not yet linked to a tenant.
// Entitlement is only fetched when a tenant is linked. Same mutate-then-reload
// model: Stripe stays the source of truth.

const subUrl = (id: string): string => `/api/admin/billing/subscriptions/${encodeURIComponent(id)}`;
const entitlementUrl = (tenantId: string): string =>
  `/api/admin/billing/tenants/${encodeURIComponent(tenantId)}/entitlement`;
const mutationHeaders = (): Record<string, string> => ({ "Content-Type": "application/json", ...getCsrfHeader() });

const failure = async (res: Response, fallback: string): Promise<{ ok: false; message: string }> => {
  const body = (await res.json().catch(() => null)) as { message?: string } | null;
  return { ok: false, message: body?.message ?? `${fallback} (${res.status})` };
};

export type UseSubscriptionBillingResult = {
  readonly status: "loading" | "ready" | "error";
  readonly subscription: Subscription | null;
  readonly entitlement: SlotEntitlement | null;
  readonly error: string | null;
  readonly reload: () => void;
  readonly addSlot: (quantity?: number) => Promise<MutationResult>;
  readonly changeItemQuantity: (stripeItemId: string, quantity: number) => Promise<MutationResult>;
  readonly removeItem: (stripeItemId: string) => Promise<MutationResult>;
  readonly setTier: (tier: string, interval: string) => Promise<MutationResult>;
  readonly sync: () => Promise<MutationResult>;
  readonly linkTenant: (tenantId: string) => Promise<MutationResult>;
  readonly openPortal: () => Promise<{ ok: true; url: string } | { ok: false; message: string }>;
};

export const useSubscriptionBilling = (
  subscriptionId: string,
  tenantId: string | null,
): UseSubscriptionBillingResult => {
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [entitlement, setEntitlement] = useState<SlotEntitlement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const opts = { credentials: "include" as const, signal: controller.signal };
    try {
      const [detailRes, entRes] = await Promise.all([
        fetch(subUrl(subscriptionId), opts),
        tenantId !== null ? fetch(entitlementUrl(tenantId), opts) : Promise.resolve(null),
      ]);
      if (!detailRes.ok) throw new Error(`subscription detail failed (${detailRes.status})`);
      const sub = (await detailRes.json()) as Subscription;
      let ent: SlotEntitlement | null = null;
      if (entRes !== null) {
        if (!entRes.ok) throw new Error(`entitlement failed (${entRes.status})`);
        ent = (await entRes.json()) as SlotEntitlement;
      }
      if (controller.signal.aborted) return;
      setSubscription(sub);
      setEntitlement(ent);
      setError(null);
      setStatus("ready");
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : String(err));
      setStatus("error");
    }
  }, [subscriptionId, tenantId]);

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load]);

  const runMutation = useCallback(
    async (path: string, method: string, body?: unknown, fallback = "request failed"): Promise<MutationResult> => {
      const res = await fetch(`${subUrl(subscriptionId)}${path}`, {
        method,
        credentials: "include",
        headers: mutationHeaders(),
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
      if (!res.ok) return failure(res, fallback);
      await load();
      return { ok: true };
    },
    [subscriptionId, load],
  );

  const addSlot = useCallback(
    (quantity = 1): Promise<MutationResult> =>
      runMutation("/items", "POST", { itemCode: INTEGRATION_SLOT_ITEM_CODE, quantity }),
    [runMutation],
  );
  const changeItemQuantity = useCallback(
    (stripeItemId: string, quantity: number): Promise<MutationResult> =>
      runMutation(`/items/${encodeURIComponent(stripeItemId)}`, "PATCH", { quantity }),
    [runMutation],
  );
  const removeItem = useCallback(
    (stripeItemId: string): Promise<MutationResult> =>
      runMutation(`/items/${encodeURIComponent(stripeItemId)}`, "DELETE"),
    [runMutation],
  );
  const setTier = useCallback(
    (tier: string, interval: string): Promise<MutationResult> => runMutation("/tier", "PUT", { tier, interval }),
    [runMutation],
  );
  const sync = useCallback((): Promise<MutationResult> => runMutation("/sync", "POST"), [runMutation]);
  const linkTenant = useCallback(
    (tid: string): Promise<MutationResult> =>
      runMutation("/tenant", "PUT", { tenantId: tid }, "couldn't link - check the tenant id is exact"),
    [runMutation],
  );

  const openPortal = useCallback(async (): Promise<
    { ok: true; url: string } | { ok: false; message: string }
  > => {
    const res = await fetch(`${subUrl(subscriptionId)}/portal-session`, {
      method: "POST",
      credentials: "include",
      headers: getCsrfHeader(),
    });
    if (!res.ok) return failure(res, "portal session failed");
    const data = (await res.json()) as { url: string };
    return { ok: true, url: data.url };
  }, [subscriptionId]);

  return {
    status,
    subscription,
    entitlement,
    error,
    reload: () => void load(),
    addSlot,
    changeItemQuantity,
    removeItem,
    setTier,
    sync,
    linkTenant,
    openPortal,
  };
};
